import { t } from "@lingui/macro";
import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router";

import { toast } from "@/client/hooks/use-toast";
import { queryClient } from "@/client/libs/query-client";

// After returning from Stripe Checkout, the plan / entitlements / balances have
// changed on the server (via webhook). Refresh everything plan-related so the
// user profile AND the whole dashboard reflect the new state immediately, on
// whichever page Stripe redirected to (jobs, settings, resumes, organizations).
const refreshBilling = () => {
  // The user query is always active (AuthGuard), so invalidating refetches it
  // without a blank flash. Subscription and organization data may be inactive
  // on the landing page, and queries use refetchOnMount:false, so we remove
  // them to force a fresh fetch wherever they are next read.
  void queryClient.invalidateQueries({ queryKey: ["user"] });
  queryClient.removeQueries({ queryKey: ["subscription"] });
  queryClient.removeQueries({ queryKey: ["organizations"] });
};

const successMessage = (checkout: string | null, tokens: string | null, pass: string | null) => {
  if (tokens === "success") {
    return { title: t`Tokens added`, description: t`Your token balance has been updated.` };
  }
  if (pass === "success") {
    return { title: t`Templates pass active`, description: t`Premium templates are now unlocked.` };
  }
  return { title: t`Subscription active`, description: t`Your plan has been updated.` };
};

export const BillingReturn = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const handled = useRef(false);

  useEffect(() => {
    const checkout = searchParams.get("checkout");
    const tokens = searchParams.get("tokens");
    const pass = searchParams.get("pass");
    // Returning from the Stripe customer portal (manage / cancel / payment method).
    const billing = searchParams.get("billing");
    const outcome = checkout ?? tokens ?? pass ?? billing;

    if (!outcome || handled.current) return;
    handled.current = true;

    let timer: ReturnType<typeof setTimeout> | undefined;

    if (outcome === "success" || billing) {
      // Refresh now and again shortly after, since the webhook (e.g. a portal
      // cancellation) can land a moment after the redirect.
      refreshBilling();
      timer = setTimeout(refreshBilling, 2500);

      // Only the explicit checkout returns get a success toast; a portal return
      // is ambiguous (they may have only viewed invoices), so stay silent there.
      if (!billing) {
        const { title, description } = successMessage(checkout, tokens, pass);
        toast({ variant: "success", title, description });
      }
    }

    // Clean the billing params from the URL in all cases.
    for (const param of ["checkout", "tokens", "pass", "billing"]) searchParams.delete(param);
    setSearchParams(searchParams, { replace: true });

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [searchParams, setSearchParams]);

  return null;
};
